import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@crave/api-client';
import type { DeleteBlockedDetails } from '@crave/shared';
import { Button } from '@crave/ui';
import { useStaff } from '../lib/staff.tsx';

const CSS = `
.crave-confirm { border: 0; padding: 0; margin: auto; width: min(520px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px); border-radius: var(--radius); background: var(--paper); color: var(--ink);
  box-shadow: 0 24px 60px rgba(20, 20, 20, 0.28); overflow: auto; overscroll-behavior: contain; }
.crave-confirm::backdrop { background: rgba(20, 20, 20, 0.55); }
.crave-confirm-body { display: grid; gap: 14px; padding: 22px; }
.crave-confirm-title { margin: 0; font-size: 22px; line-height: 1.25; overflow-wrap: anywhere; }
.crave-confirm-text { margin: 0; color: var(--ink-soft); }
.crave-confirm-list { margin: 0; padding: 12px 14px 12px 32px; border-radius: var(--radius-sm);
  background: var(--mist); display: grid; gap: 4px; }
.crave-confirm-list-title { margin: 0 0 -8px; font-weight: 600; }
.crave-confirm-input { width: 100%; min-width: 0; min-height: var(--touch); padding: 0 12px; border-radius: var(--radius-sm);
  border: 2px solid var(--line); background: var(--paper); color: var(--ink); }
.crave-confirm-area { width: 100%; min-width: 0; padding: 10px 12px; border-radius: var(--radius-sm); resize: vertical;
  border: 2px solid var(--line); background: var(--paper); color: var(--ink); }
.crave-confirm-label { display: grid; gap: 6px; }
.crave-confirm-label > span:first-child { font-weight: 600; line-height: 1.3; }
.crave-confirm-hint { font-size: 14px; color: var(--ink-soft); }
.crave-confirm-alert { border: 2px solid var(--crave-red); color: var(--ink); border-radius: var(--radius-sm);
  padding: 10px 12px; font-size: 15px; display: grid; gap: 4px; }
.crave-confirm-alert strong { color: var(--crave-red); }
.crave-confirm-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; padding-top: 4px; }
@media (max-width: 480px) {
  .crave-confirm-body { padding: 18px 16px; }
  .crave-confirm-actions { flex-direction: column-reverse; }
  .crave-confirm-actions > button { width: 100%; }
}
`;

/** Compare what was typed with the name, forgiving case, extra spaces and iPhone curly quotes. */
const normalize = (s: string) =>
  s.replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, ' ').trim().toLowerCase();

export interface ConfirmDeleteAlternative {
  /** Button text, e.g. "Deactivate", "Retire from menu", "Erase personal details", "Void". */
  label: string;
  /** Runs the alternative. Resolve to close the dialog; throw to show the error in it. */
  onClick: (input: { reason: string | undefined }) => unknown;
  /** One plain sentence shown above the button, e.g. "It stays in past orders but leaves the menu." */
  description?: string | undefined;
  /** Text while it runs (default "Working…"). */
  pendingLabel?: string | undefined;
}

export interface ConfirmDeleteProps {
  open: boolean;
  /** Called on Cancel, Esc, a click outside, and after a successful delete or alternative. */
  onClose: () => void;
  /** e.g. "Delete Fresh basil?" */
  title: string;
  description?: ReactNode;
  /** What else goes with it, one item per line ("2 packages", "Its stock count at Mobile 1"). */
  consequences?: readonly ReactNode[] | undefined;
  /** Heading above the consequences list (default "This also deletes:"). */
  consequencesTitle?: string | undefined;
  /** When set, the action stays disabled until this name is typed. */
  confirmText?: string | undefined;
  /** Adds an optional "Reason" box; its text is passed to onConfirm and kept in the audit trail. */
  askReason?: boolean | undefined;
  /** Danger button text (default "Delete") and its text while running (default "Deleting…"). */
  actionLabel?: string | undefined;
  pendingLabel?: string | undefined;
  /**
   * Heading shown when the API refuses (DELETE_BLOCKED). Default comes from actionLabel:
   * "This can't be deleted.", "This can't be voided.", "This can't be removed."
   */
  blockedTitle?: string | undefined;
  /**
   * Runs the delete. Resolve to close the dialog. Throw (for example the ApiError from the API) to
   * keep it open: a DELETE_BLOCKED error shows the API's sentence and the alternative button.
   */
  onConfirm: (input: { reason: string | undefined }) => unknown;
  /** Offered when the API says the record cannot be deleted (and whenever `showAlternative`). */
  alternative?: ConfirmDeleteAlternative | undefined;
  /** Show the alternative from the start, not only after a DELETE_BLOCKED answer. */
  showAlternative?: boolean | undefined;
  /** Called after onConfirm resolves, before the dialog closes. */
  onDone?: (() => void) | undefined;
  /**
   * Permission needed to see this dialog at all (default 'records.delete'). Recipe drafts pass
   * 'recipes.manage'. Pass null to skip the check.
   */
  permission?: string | null | undefined;
}

type Problem = { blocked: boolean; message: string; uses: Record<string, number> };

/** The verb of an action button, as it reads after "This can't be". */
const DONE: Record<string, string> = {
  delete: 'deleted',
  remove: 'removed',
  void: 'voided',
  erase: 'erased',
  discard: 'discarded',
  close: 'closed',
  retire: 'retired',
  deactivate: 'deactivated',
  archive: 'archived',
  cancel: 'canceled',
  clear: 'cleared',
  reset: 'reset',
};

/**
 * The heading when the API refuses, worded from the action button: "Delete" gives "This can't be
 * deleted.", "Void receipt" gives "This can't be voided.". Other actions: "This can't be done."
 */
export function blockedHeading(actionLabel: string): string {
  const verb = actionLabel.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  const done = DONE[verb];
  return done ? `This can't be ${done}.` : "This can't be done.";
}

function problemFrom(err: unknown): Problem {
  if (err instanceof ApiError) {
    if (err.code === 'DELETE_BLOCKED') {
      const details = err.details as Partial<DeleteBlockedDetails> | undefined;
      return { blocked: true, message: err.message, uses: details?.uses ?? {} };
    }
    return { blocked: false, message: err.message, uses: {} };
  }
  return {
    blocked: false,
    message: "Couldn't reach the Crave server. Check the connection and try again.",
    uses: {},
  };
}

/**
 * The one confirmation for anything destructive: a native modal dialog (the browser traps focus,
 * Esc closes it), the record's name in the title, what goes with it, an optional typed name, and a
 * red action button. When the API refuses with DELETE_BLOCKED it shows why and offers the safe
 * alternative the caller passed.
 */
export function ConfirmDelete({
  open,
  onClose,
  title,
  description,
  consequences,
  consequencesTitle = 'This also deletes:',
  confirmText,
  askReason = false,
  actionLabel = 'Delete',
  pendingLabel = 'Deleting…',
  blockedTitle,
  onConfirm,
  alternative,
  showAlternative = false,
  onDone,
  permission = 'records.delete',
}: ConfirmDeleteProps) {
  const staff = useStaff();
  const allowed = permission === null || staff.can(permission);
  const ref = useRef<HTMLDialogElement>(null);
  const typedRef = useRef<HTMLInputElement>(null);
  const ids = useId();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<'delete' | 'alternative' | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const openNow = open && allowed;

  // Open and close the native dialog from the `open` prop; start fresh every time it opens.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (openNow && !dialog.open) {
      setTyped('');
      setReason('');
      setPending(null);
      setProblem(null);
      dialog.showModal();
      // Start on the safe choice: the name box when one is asked for, otherwise Cancel.
      const first = confirmText
        ? typedRef.current
        : dialog.querySelector<HTMLButtonElement>('[data-confirm-cancel]');
      first?.focus();
    } else if (!openNow && dialog.open) {
      dialog.close();
    }
  }, [openNow, confirmText]);

  // The page behind a modal still scrolls on iPhone; hold it while the dialog is open.
  useEffect(() => {
    if (!openNow) return;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = before;
    };
  }, [openNow]);

  if (!allowed) return null;

  const busy = pending !== null;
  const nameOk = !confirmText || normalize(typed) === normalize(confirmText);
  const reasonValue = reason.trim() || undefined;
  const offerAlternative = alternative && (showAlternative || problem?.blocked);

  const close = () => {
    if (!busy) onClose();
  };

  const run = async (which: 'delete' | 'alternative') => {
    const action = which === 'delete' ? onConfirm : alternative?.onClick;
    if (!action) return;
    setPending(which);
    setProblem(null);
    try {
      await action({ reason: reasonValue });
      setPending(null);
      if (which === 'delete') onDone?.();
      onClose();
    } catch (err) {
      setPending(null);
      setProblem(problemFrom(err));
    }
  };

  return (
    <dialog
      ref={ref}
      className="crave-confirm"
      aria-labelledby={`${ids}-title`}
      aria-describedby={description && !problem?.blocked ? `${ids}-desc` : undefined}
      onCancel={(e) => {
        // Esc: never close halfway through a request; otherwise let the parent close it.
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself.
        if (e.target === e.currentTarget) close();
      }}
    >
      <style href="crave-confirm-delete" precedence="default">
        {CSS}
      </style>
      <form
        className="crave-confirm-body"
        onSubmit={(e) => {
          e.preventDefault();
          if (nameOk && !busy && !problem?.blocked) void run('delete');
        }}
      >
        <h2 id={`${ids}-title`} className="crave-confirm-title">
          {title}
        </h2>
        {/* Once the API refuses, its sentence replaces the caller's description of what would happen. */}
        {description && !problem?.blocked && (
          <div id={`${ids}-desc`} className="crave-confirm-text">
            {description}
          </div>
        )}
        {consequences && consequences.length > 0 && !problem?.blocked && (
          <>
            <p className="crave-confirm-list-title">{consequencesTitle}</p>
            <ul className="crave-confirm-list">
              {consequences.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </>
        )}
        {confirmText && !problem?.blocked && (
          <label className="crave-confirm-label">
            <span>
              Type <strong>{confirmText}</strong> to confirm
            </span>
            <input
              ref={typedRef}
              className="crave-confirm-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={busy}
              aria-describedby={`${ids}-typehint`}
            />
            <span id={`${ids}-typehint`} className="crave-confirm-hint">
              This can't be undone.
            </span>
          </label>
        )}
        {askReason && !problem?.blocked && (
          <label className="crave-confirm-label">
            <span>Reason (optional)</span>
            <textarea
              className="crave-confirm-area"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
            <span className="crave-confirm-hint">Kept in the audit trail.</span>
          </label>
        )}
        {problem && (
          <div className="crave-confirm-alert" role="alert">
            {problem.blocked && <strong>{blockedTitle ?? blockedHeading(actionLabel)}</strong>}
            <span>{problem.message}</span>
          </div>
        )}
        {offerAlternative && alternative.description && (
          <p className="crave-confirm-text">{alternative.description}</p>
        )}
        <div className="crave-confirm-actions">
          <Button
            data-confirm-cancel=""
            type="button"
            variant="secondary"
            onClick={close}
            disabled={busy}
          >
            {problem?.blocked ? 'Close' : 'Cancel'}
          </Button>
          {offerAlternative && (
            <Button
              type="button"
              variant={problem?.blocked ? 'primary' : 'secondary'}
              disabled={busy}
              onClick={() => void run('alternative')}
            >
              {pending === 'alternative'
                ? (alternative.pendingLabel ?? 'Working…')
                : alternative.label}
            </Button>
          )}
          {!problem?.blocked && (
            <Button
              type="submit"
              variant="danger"
              disabled={!nameOk || busy}
              aria-busy={pending === 'delete' || undefined}
              style={{ background: 'var(--crave-red)', color: '#fff' }}
            >
              {pending === 'delete' ? pendingLabel : actionLabel}
            </Button>
          )}
        </div>
      </form>
    </dialog>
  );
}
